import { translate } from '../../i18n/index.js';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  BoltIcon,
  BuildingIcon,
  LockIcon,
  useAtomFitDarkScheme,
} from '../../components/design/AtomFitUI.js';
import { LandingFooter } from '../../components/landing/LandingFooter.js';
import { PricingSection } from '../../components/landing/PricingSection.js';

const steps = [
  [
    '01',

    translate('pages.public.landing.module.book'),
    translate('pages.public.landing.module.booking_select'),
  ],

  [
    '02',

    translate('pages.public.landing.module.steps'),
    translate('pages.public.landing.module.email_sms_access_code'),
  ],

  [
    '03',

    translate('pages.public.landing.module.training'),
    translate('pages.public.landing.module.booking_open'),
  ],
];

const productStats = [
  ['24/7', translate('pages.public.landing.module.product_stats')],
  [
    translate('pages.public.landing.module.product_stats_2'),
    translate('pages.public.landing.module.booking'),
  ],

  ['100 %', translate('pages.public.landing.module.product_stats_3')],
  ['0', translate('pages.public.landing.module.product_stats_4')],
];

const productPoints = [
  {
    icon: <LockIcon />,
    title: translate('pages.public.landing.product_points.title'),
    desc: translate('pages.public.landing.product_points.desc'),
  },
  {
    icon: <BoltIcon />,
    title: translate('pages.public.landing.product_points.title_2'),
    desc: translate('pages.public.landing.product_points.desc_2'),
  },
  {
    icon: <BuildingIcon />,
    title: translate('pages.public.landing.product_points.title_3'),
    desc: translate('pages.public.landing.product_points.desc_3'),
  },
];

const heroImages = [
  '/brand/landing/gym-hero-1.jpg',
  '/brand/landing/gym-hero-2.jpg',
  '/brand/landing/gym-hero-3.jpg',
];

const faqs = [
  [
    translate('pages.public.landing.module.access_code'),
    translate('pages.public.landing.module.booking_email_sms'),
  ],

  [
    translate('pages.public.landing.module.booking_cancel'),
    translate('pages.public.landing.module.booking_2'),
  ],

  [
    translate('pages.public.landing.module.book_gym'),
    translate('pages.public.landing.module.book_booking_membership'),
  ],

  [
    translate('pages.public.landing.module.next'),
    translate('pages.public.landing.module.booking_add'),
  ],

  [
    translate('pages.public.landing.module.faqs'),
    translate('pages.public.landing.module.booking_gym'),
  ],

  [translate('pages.public.landing.module.faqs_2'), translate('pages.public.landing.module.gym')],

  [
    translate('pages.public.landing.module.membership'),
    translate('pages.public.landing.module.membership_2'),
  ],

  [
    translate('pages.public.landing.module.equipment'),
    translate('pages.public.landing.module.gym_equipment'),
  ],

  [
    translate('pages.public.landing.module.faqs_3'),
    translate('pages.public.landing.module.faqs_4'),
  ],

  [
    translate('pages.public.landing.module.training_2'),
    translate('pages.public.landing.module.select'),
  ],
];

function LandingCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const pointerReadyRef = useRef(false);
  const [isTarget, setIsTarget] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isPointerReady, setIsPointerReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia('(pointer: fine)').matches) return;

    setIsEnabled(true);
    document.documentElement.classList.add('af-cursor-none-root');

    const cursor = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };
    const ring = { ...cursor };
    let animationFrame = 0;

    const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount;

    const render = () => {
      ring.x = lerp(ring.x, cursor.x, 0.18);
      ring.y = lerp(ring.y, cursor.y, 0.18);

      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) translate(-50%, -50%)`;
      }

      animationFrame = requestAnimationFrame(render);
    };

    const updateDot = () => {
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${cursor.x}px, ${cursor.y}px, 0) translate(-50%, -50%)`;
      }
    };

    const updateTargetState = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      setIsTarget(
        Boolean(
          target?.closest(
            'a, button, [role="button"], input, textarea, select, label, .cursor-target',
          ),
        ),
      );
    };

    const handlePointerMove: EventListener = (event) => {
      if (!(event instanceof MouseEvent)) return;

      cursor.x = event.clientX;
      cursor.y = event.clientY;
      if (!pointerReadyRef.current) {
        ring.x = cursor.x;
        ring.y = cursor.y;
        if (ringRef.current) {
          ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) translate(-50%, -50%)`;
        }
        pointerReadyRef.current = true;
        setIsPointerReady(true);
      }
      updateDot();
      updateTargetState(event);
    };

    updateDot();
    animationFrame = requestAnimationFrame(render);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('mousemove', handlePointerMove, { passive: true });
    window.addEventListener('pointerrawupdate', handlePointerMove, { passive: true });

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('pointerrawupdate', handlePointerMove);
      document.documentElement.classList.remove('af-cursor-none-root');
    };
  }, []);

  if (!isEnabled) return null;

  return createPortal(
    <>
      <div
        ref={ringRef}
        className={`af-landing-cursor af-landing-cursor--ring${isPointerReady ? ' is-ready' : ''}`}
        aria-hidden="true"
      >
        <div className={isTarget ? 'is-target' : undefined} />
      </div>
      <div
        ref={dotRef}
        className={`af-landing-cursor af-landing-cursor--dot${isPointerReady ? ' is-ready' : ''}`}
        aria-hidden="true"
      >
        <div className={isTarget ? 'is-target' : undefined} />
      </div>
    </>,
    document.body,
  );
}

export function Landing() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [activeHeroImage, setActiveHeroImage] = useState(0);
  const isDarkScheme = useAtomFitDarkScheme();

  useEffect(() => {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let carouselInterval = 0;

    const syncCarousel = () => {
      if (carouselInterval) window.clearInterval(carouselInterval);
      carouselInterval = 0;

      if (reducedMotionQuery.matches) {
        setActiveHeroImage(0);
        return;
      }

      carouselInterval = window.setInterval(() => {
        setActiveHeroImage((current) => (current + 1) % heroImages.length);
      }, 5_500);
    };

    syncCarousel();
    reducedMotionQuery.addEventListener('change', syncCarousel);

    return () => {
      if (carouselInterval) window.clearInterval(carouselInterval);
      reducedMotionQuery.removeEventListener('change', syncCarousel);
    };
  }, []);

  useEffect(() => {
    const revealItems = Array.from(document.querySelectorAll<HTMLElement>('.af-scroll-reveal'));

    if (!('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -14% 0px', threshold: 0.14 },
    );

    revealItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="af-screen af-landing">
      <LandingCursor />
      <section className="af-landing-hero">
        <div className="af-landing-hero-media" aria-hidden="true">
          {heroImages.map((src, index) => (
            <img
              className={index === activeHeroImage ? 'is-active' : undefined}
              src={src}
              alt=""
              decoding="async"
              key={src}
            />
          ))}
        </div>
        <div className="af-shell af-landing-hero-grid">
          <div className="af-landing-hero-copy">
            <h1 className="af-hero-claim">
              <span className="af-hero-claim-line--last">
                <span className="af-hero-claim-line">
                  {translate('pages.public.landing.landing.span_hero_claim_line.copy')}
                </span>
                <span className="af-hero-claim-line">
                  {translate('pages.public.landing.landing.span_hero_claim_line.copy_2')}
                  <span className="af-hero-claim-stop">{'.'}</span>
                </span>
              </span>
            </h1>
            <p className="af-landing-lede">
              {translate('pages.public.landing.landing.p_landing_lede.description')}

              <br />
              <br />
              {translate('pages.public.landing.landing.p_landing_lede.description_2')}

              <br />
              {translate('pages.public.landing.landing.p_landing_lede.open')}
            </p>
            <div className="af-landing-actions">
              <Link className="af-primary-btn" to="/gyms">
                {translate('pages.public.landing.landing.link_primary_btn.book')}

                <svg
                  className="af-hero-cta-arrow"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />

                  <path
                    d="m13 6 6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
        <a
          className="af-hero-scroll-cue"
          href="#how-it-works"
          aria-label={translate('pages.public.landing.landing.a_hero_scroll_cue.aria_label_next')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m6 9 6 6 6-6"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </section>

      <section
        className="af-shell af-landing-section af-landing-steps af-landing-surface--off af-scroll-reveal"
        id="how-it-works"
      >
        <div className="af-landing-section-head">
          <p className="af-kicker">
            {translate('pages.public.landing.landing.p_kicker.how_it_works')}
          </p>
          <h2>
            <span className="af-landing-steps-title-main">
              {translate('pages.public.landing.landing.span_landing_steps_title_main.book')}
              <span className="af-landing-title-dot">{'.'}</span>{' '}
              {translate('pages.public.landing.landing.span_landing_steps_title_main.copy')}
              <span className="af-landing-title-dot">{'.'}</span>
            </span>
            <span className="af-landing-steps-title-accent">
              {translate('pages.public.landing.landing.span_landing_steps_title_accent.training')}
              <span className="af-landing-title-dot">{'.'}</span>
            </span>
          </h2>
        </div>
        <div className="af-landing-step-grid">
          {steps.map(([number, title, desc]) => (
            <article className="af-landing-step" key={number}>
              <span className="af-landing-step-number">{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <PricingSection />

      <section
        className="af-shell af-landing-section af-landing-faq af-landing-surface--off af-scroll-reveal"
        id="faq"
      >
        <div className="af-landing-section-head">
          <p className="af-kicker">{translate('pages.public.landing.landing.p_kicker.faq')}</p>
          <h2>
            {translate('pages.public.landing.landing.h2.behind_closed_doors_heading')}
            <span className="af-landing-title-dot">{'.'}</span>
          </h2>
        </div>
        <div className="af-landing-faq-list">
          {faqs.map(([question, answer], index) => {
            const isOpen = openFaqIndex === index;

            return (
              <article className="af-landing-faq-item" key={question}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  onClick={() => setOpenFaqIndex((current) => (current === index ? null : index))}
                >
                  <span>{question}</span>
                  <i aria-hidden="true" />
                </button>
                <div
                  id={`faq-answer-${index}`}
                  className="af-landing-faq-answer"
                  aria-hidden={!isOpen}
                  onClick={() => {
                    if (isOpen) setOpenFaqIndex(null);
                  }}
                >
                  <p>{answer}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section
        className="af-landing-claim-section"
        aria-label={translate(
          'pages.public.landing.landing.section_landing_claim_section.aria_label_content',
        )}
      >
        <img
          className="af-landing-claim-image"
          src={
            isDarkScheme
              ? '/brand/form-your-element-dark-burn-dark.svg'
              : '/brand/form-your-element-dark-burn.svg'
          }
          alt={translate('pages.public.landing.landing.img_landing_claim_image.alt_content')}
        />
      </section>

      <LandingFooter />
    </main>
  );
}
